import { useState, useMemo } from "react";
import { Radio, ThumbsUp, MessageSquare, RefreshCw, ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { useLastSync, formatSyncAge } from "@/hooks/useLastSync";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AccountFilterSidebar, FilterSection } from "@/components/accounts/AccountFilterSidebar";
import AddProfileDialog from "@/components/company-tracking/AddProfileDialog";
import PostDetailModal from "@/components/company-tracking/PostDetailModal";
import { useCategorySummary, useProfileSummary, usePostsTable } from "@/hooks/useCompanyTracking";
import type { PostFilters, PostCard } from "@/types/company-tracking";
import { format } from "date-fns";

const CompanyTracking = () => {
  const [search, setSearch] = useState("");
  const [filterValues, setFilterValues] = useState<Record<string, string[]>>({});
  const [page, setPage] = useState(1);
  const [selectedPost, setSelectedPost] = useState<PostCard | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');

  const toggleSort = (col: string) => {
    if (sortColumn === col) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(col);
      setSortDirection('desc');
    }
  };

  const SortIcon = ({ col }: { col: string }) => {
    if (sortColumn !== col) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-40" />;
    return sortDirection === 'asc' ? <ArrowUp className="h-3 w-3 ml-1" /> : <ArrowDown className="h-3 w-3 ml-1" />;
  };

  const { data: lastSync } = useLastSync('company_tracking');
  const syncAge = formatSyncAge(lastSync?.completed_at ?? null);

  const { data: categories = [] } = useCategorySummary();
  const { data: profiles = [] } = useProfileSummary();

  const filters: PostFilters = useMemo(() => ({
    category: filterValues.category,
    profile: filterValues.profile,
    dateRange: filterValues.dateRange?.[0],
  }), [filterValues]);

  const { data: postsResult, isLoading } = usePostsTable(filters, search, page);
  const rawPosts = useMemo(() => postsResult?.data || [], [postsResult?.data]);
  const posts = useMemo(() => {
    if (!sortColumn) return rawPosts;
    return [...rawPosts].sort((a, b) => {
      const aVal = (a as any)[sortColumn] ?? '';
      const bVal = (b as any)[sortColumn] ?? '';
      if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
      if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
      return 0;
    });
  }, [rawPosts, sortColumn, sortDirection]);
  const totalCount = postsResult?.count || 0;
  const pageSize = postsResult?.pageSize || 50;
  const totalPages = Math.ceil(totalCount / pageSize);

  const filterSections: FilterSection[] = useMemo(() => [
    {
      id: "category",
      label: "Category",
      options: categories.map(c => ({
        value: c.category,
        label: c.display_name || c.category,
        count: c.total_posts,
      })),
    },
    {
      id: "profile",
      label: "Tracked Profile",
      options: profiles.map(p => ({
        value: p.profile_name,
        label: p.profile_name,
        count: p.total_posts,
      })),
    },
    {
      id: "dateRange",
      label: "Date Range",
      options: [
        { value: "7d", label: "Last 7 days" },
        { value: "30d", label: "Last 30 days" },
        { value: "90d", label: "Last 90 days" },
        { value: "all", label: "All time" },
      ],
    },
  ], [categories, profiles]);

  const handleFilterChange = (sectionId: string, values: string[]) => {
    setFilterValues(prev => ({ ...prev, [sectionId]: values }));
    setPage(1);
  };

  const handleClearFilters = () => {
    setFilterValues({});
    setPage(1);
  };

  const hasActiveFilters = Object.values(filterValues).some(v => v.length > 0);

  const handleRowClick = (post: PostCard) => {
    setSelectedPost(post);
    setModalOpen(true);
  };

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold text-foreground">Social Listening - Accounts</h1>
            <Badge variant={syncAge.isStale ? "destructive" : "secondary"} className="text-xs flex items-center gap-1">
              <RefreshCw className="h-3 w-3" />
              Synced {syncAge.label}
            </Badge>
          </div>
          <p className="text-sm text-muted-foreground">
            Monitor posts from tracked company profiles and see who engages
          </p>
        </div>
        <AddProfileDialog />
      </div>

      {/* Main Content */}
      <div className="flex flex-1 overflow-hidden">
        {/* Filter Sidebar */}
        <AccountFilterSidebar
          totalCount={totalCount}
          searchPlaceholder="Search posts, profiles..."
          searchValue={search}
          onSearchChange={(value) => {
            setSearch(value);
            setPage(1);
          }}
          filterSections={filterSections}
          filterValues={filterValues}
          onFilterChange={handleFilterChange}
          onClearFilters={handleClearFilters}
          hasActiveFilters={hasActiveFilters}
        />

        {/* Table Area */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {isLoading ? (
            <div className="flex items-center justify-center flex-1">
              <Radio className="h-8 w-8 text-muted-foreground animate-pulse" />
            </div>
          ) : posts.length === 0 ? (
            <div className="flex flex-col items-center justify-center flex-1 text-center">
              <Radio className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-medium text-foreground">No posts found</h3>
              <p className="text-muted-foreground mt-1">
                {hasActiveFilters || search
                  ? "Try adjusting your filters or search"
                  : "Add company profiles to start tracking their social activity"}
              </p>
            </div>
          ) : (
            <>
              {/* Table */}
              <div className="flex-1 overflow-auto">
                <Table>
                  <TableHeader className="sticky top-0 bg-background z-10">
                    <TableRow>
                      <TableHead className="w-[160px]">Profile</TableHead>
                      <TableHead>Post Content</TableHead>
                      <TableHead className="w-[100px] cursor-pointer select-none" onClick={() => toggleSort('post_date')}>
                        <span className="inline-flex items-center">Date<SortIcon col="post_date" /></span>
                      </TableHead>
                      <TableHead className="w-[80px] text-center cursor-pointer select-none" onClick={() => toggleSort('likes')}>
                        <span className="inline-flex items-center justify-center">Likes<SortIcon col="likes" /></span>
                      </TableHead>
                      <TableHead className="w-[80px] text-center cursor-pointer select-none" onClick={() => toggleSort('comments')}>
                        <span className="inline-flex items-center justify-center">Comments<SortIcon col="comments" /></span>
                      </TableHead>
                      <TableHead className="w-[100px] text-center cursor-pointer select-none" onClick={() => toggleSort('engagement_count')}>
                        <span className="inline-flex items-center justify-center">Engaged<SortIcon col="engagement_count" /></span>
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {posts.map((post, idx) => (
                      <TableRow
                        key={post.post_url || idx}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleRowClick(post)}
                      >
                        <TableCell className="font-medium">
                          {post.monitoring_profile}
                        </TableCell>
                        <TableCell>
                          <span className="line-clamp-2 text-sm text-muted-foreground">
                            {post.post_content?.slice(0, 120)}
                            {(post.post_content?.length || 0) > 120 && "..."}
                          </span>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {post.post_date ? format(new Date(post.post_date), "MMM d") : "-"}
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1 text-muted-foreground">
                            <ThumbsUp className="h-3 w-3" />
                            <span className="text-sm">{post.likes}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center">
                          <div className="flex items-center justify-center gap-1 text-muted-foreground">
                            <MessageSquare className="h-3 w-3" />
                            <span className="text-sm">{post.comments}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-center text-muted-foreground text-sm">
                          {post.engagement_count}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between px-4 py-3 border-t bg-muted/30">
                  <span className="text-sm text-muted-foreground">
                    Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, totalCount)} of {totalCount}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <span className="text-sm">
                      Page {page} of {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {/* Post Detail Modal */}
      <PostDetailModal
        post={selectedPost}
        open={modalOpen}
        onOpenChange={setModalOpen}
      />
    </div>
  );
};

export default CompanyTracking;
